import { CircularProgress, Paper, Stack } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import CustomButton from '@/components/inputs/CustomButton'
import PageHeader from '@/components/layouts/PageHeader'
import { useAlertContext } from '@/contexts/AlertContext'
import { PrequalificationRouteInformation } from '@/features/prequalification/constants'
import AddResourcesPanel from '@/features/prequalification/localControl/components/AddResourcesPanel'
import ResourceSetResourceBasket from '@/features/prequalification/localControl/components/ResourceSetResourceBasket'
import { useAddResourceSetResourcesMutation } from '@/features/prequalification/localControl/hooks/useAddResourceSetResourcesMutation'
import {
  useInvalidateLocalPqResourceSetQuery,
  useLocalPqResourceSetQuery,
} from '@/features/prequalification/localControl/hooks/useLocalPqResourceSetQuery'
import { useLocalPqResourceSetRunsQuery } from '@/features/prequalification/localControl/hooks/useLocalPqResourceSetRunsQuery'
import { useRemoveResourceSetResourceMutation } from '@/features/prequalification/localControl/hooks/useRemoveResourceSetResourceMutation'
import { useResourceBasket } from '@/features/prequalification/localControl/hooks/useResourceBasket'
import type { ResourceSetMember } from '@/features/prequalification/localControl/types'
import { isTerminalPhase } from '@/features/prequalification/types'
import { errorHandler } from '@/utils/errorHandler'

/**
 * Full-page resource editor for an existing resource set — the same two-pane view as the
 * builder (basket on the left, add-panel on the right), seeded with the resource set's
 * current resources. Edits are staged locally; Save commits the diff (adds the new
 * resources, removes the dropped ones) and returns to the detail page. Removal is
 * gated: a resource with active (non-terminal) runs can't be dropped, and the set
 * must keep at least one resource.
 */
function EditResourceSetResources() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { resourceSetId = '' } = useParams()
  const { pushAlert } = useAlertContext()

  const { resourceSet, isLoading: isResourceSetLoading } = useLocalPqResourceSetQuery(
    { resourceSetId },
    { enabled: !!resourceSetId },
  )
  const {
    runs,
    isLoading: areRunsLoading,
    isError: isRunsError,
  } = useLocalPqResourceSetRunsQuery({ resourceSetId }, { enabled: !!resourceSetId })
  // When run status can't be determined (still loading or the fetch failed) we can't
  // trust the active-run set, so removing a persisted resource must fail safe rather
  // than silently allow dropping a resource that may have runs in flight.
  const isRunStatusUnknown = areRunsLoading || isRunsError

  const { addResourceSetResources } = useAddResourceSetResourcesMutation({ resourceSetId })
  const { removeResourceSetResource } = useRemoveResourceSetResourceMutation({ resourceSetId })
  const invalidateResourceSet = useInvalidateLocalPqResourceSetQuery()

  const { resources, selectedIds, toggleResource, removeResource, reset } = useResourceBasket()

  const [originalIds, setOriginalIds] = useState<Set<string>>(new Set())
  const [isSeeded, setIsSeeded] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Seed the working set from the resource set once it loads, snapshotting the original
  // ids the Save diff is taken against.
  useEffect(() => {
    if (resourceSet && !isSeeded) {
      reset(resourceSet.resources)
      setOriginalIds(new Set(resourceSet.resources.map((resource) => resource.resourceId)))
      setIsSeeded(true)
    }
  }, [resourceSet, isSeeded, reset])

  const resourceIdsWithActiveRuns = useMemo(
    () => new Set((runs ?? []).filter((run) => !isTerminalPhase(run.phase)).map((run) => run.resourceId)),
    [runs],
  )

  const addedIds = resources.filter((resource) => !originalIds.has(resource.resourceId)).map((r) => r.resourceId)
  const removedIds = Array.from(originalIds).filter((id) => !selectedIds.has(id))
  const changeCount = addedIds.length + removedIds.length
  const canSave = changeCount > 0 && resources.length > 0 && !isSaving

  // Only persisted resources are gated; resources added in this session never have
  // runs, so they stay freely removable. A persisted resource is locked when it has
  // active runs, or — fail-safe — while run status is unknown (loading or fetch error).
  function removalBlockReason(resource: ResourceSetMember): string | null {
    if (!originalIds.has(resource.resourceId)) return null
    if (resourceIdsWithActiveRuns.has(resource.resourceId)) {
      return t('prequalifications.resource_set.resources.remove_blocked_active_runs')
    }
    if (isRunStatusUnknown) {
      return t('prequalifications.resource_set.resources.remove_blocked_run_status_unknown')
    }
    return null
  }

  async function handleSave() {
    if (!canSave) return

    setIsSaving(true)
    try {
      if (addedIds.length > 0) await addResourceSetResources({ resourceIds: addedIds })
      await Promise.all(removedIds.map((id) => removeResourceSetResource(id)))
      await invalidateResourceSet()
      pushAlert({ message: t('prequalifications.resource_set.edit_resources.save_success'), severity: 'success' })
      navigate(PrequalificationRouteInformation.RESOURCE_SET_DETAILS.navigationPath(resourceSetId))
    } catch (err) {
      const error = errorHandler(err, t('prequalifications.resource_set.edit_resources.save_error'))
      pushAlert({ message: error.message, severity: 'error' })
      await invalidateResourceSet()
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <>
      <PageHeader
        hiddenTitle
        breadcrumbItems={[
          {
            text: t('component.page_header.prequalifications'),
            to: PrequalificationRouteInformation.INDEX.navigationPath(),
          },
          {
            text: t('prequalifications.scope.local_control'),
            to: PrequalificationRouteInformation.INDEX.navigationPath(),
          },
          {
            text: resourceSet?.name ?? t('prequalifications.resource_set.title'),
            to: PrequalificationRouteInformation.RESOURCE_SET_DETAILS.navigationPath(resourceSetId),
          },
        ]}
        pageTag="prequalifications-edit-resources"
        pageTitle={t('prequalifications.resource_set.edit_resources.title')}
      />

      <Stack
        sx={{
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <CustomTypography noWrap sx={{ minWidth: 0 }} variant="h1">
          {t('prequalifications.resource_set.edit_resources.title')}
        </CustomTypography>

        <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          <CustomButton
            disabled={isSaving}
            variant="outlined"
            onClick={() =>
              navigate(PrequalificationRouteInformation.RESOURCE_SET_DETAILS.navigationPath(resourceSetId))
            }
          >
            {t('common.button.cancel')}
          </CustomButton>
          <CustomButton
            data-testid="save-resources-button"
            disabled={!canSave}
            variant="contained"
            onClick={handleSave}
          >
            {t('prequalifications.resource_set.edit_resources.save', { count: changeCount })}
          </CustomButton>
        </Stack>
      </Stack>

      {isResourceSetLoading ? (
        <Stack sx={{ alignItems: 'center', py: 6 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <Stack sx={{ flexDirection: { xs: 'column', md: 'row' }, gap: 3, mt: 3, alignItems: 'stretch' }}>
          <Paper sx={{ p: 3, flex: 1, minWidth: 0 }}>
            <ResourceSetResourceBasket
              removalBlockReason={removalBlockReason}
              resources={resources}
              onRemove={removeResource}
            />
          </Paper>
          <Paper sx={{ p: 3, width: { xs: '100%', md: 400 }, flexShrink: 0 }}>
            <AddResourcesPanel selectedIds={selectedIds} onToggle={toggleResource} />
          </Paper>
        </Stack>
      )}
    </>
  )
}

export default EditResourceSetResources
